import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRunDraftInputSchema,
  runConditionsFormSchema,
  runMeasurementsFormSchema,
  type RunConditionsFormValues,
  type RunMeasurementsFormValues,
  type TestType,
} from 'fuel-benchmark-contracts';
import { ArrowLeft, ArrowRight, CheckCircle2, Save } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orpc } from '../../../apis/orpc-query';
import { Button } from '../../../components/ui/Button';
import { DefinitionList, type DefinitionItem } from '../../../components/ui/DefinitionList';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Spinner } from '../../../components/ui/Spinner';
import { StatusText } from '../../../components/ui/StatusText';
import { cn } from '../../../lib/cn';
import { formatDay, formatKm, formatText } from '../../../lib/format';
import { serverMessage, submitErrorKey, textFieldOptions } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { TEST_TYPES, trafficSummary } from '../../conditions/condition-display';
import { RunFieldList } from '../RunFieldList';
import {
  CONDITION_GROUPS,
  isHybridPowertrain,
  MEASUREMENT_GROUPS,
  prefillConditions,
  type RunSessionValues,
  toConditionsFormValues,
  toMeasurementsFormValues,
  toSessionValues,
  withExplicitNulls,
} from '../run-fields';
import { PickerList, type PickerOption } from './PickerList';
import { RunFieldGroups } from './RunFieldGroups';
import { StepIndicator } from './StepIndicator';

const CATALOGUE_PAGE_SIZE = 200;

/**
 * Six steps (plan §7). The third one collects the route *and* the session facts — test date, driver, times —
 * because a run's identity is fixed when the draft is created and never edited afterwards; an idle test simply
 * has no route half to fill in.
 */
const STEP = { vehicle: 0, scenario: 1, session: 2, conditions: 3, measurements: 4, review: 5 } as const;

const STEP_LABEL_KEYS = [
  'runs.wizard.steps.vehicle',
  'runs.wizard.steps.scenario',
  'runs.wizard.steps.session',
  'runs.wizard.steps.conditions',
  'runs.wizard.steps.measurements',
  'runs.wizard.steps.review',
] as const;

/** The draft-creating half of the contract input; vehicle, scenario and route come from the picker steps. */
const sessionSchema = createRunDraftInputSchema.omit({ vehicleId: true, scenarioId: true, routeId: true });

const today = (): string => new Date().toISOString().slice(0, 10);

export const NewRunWizardPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const resumeRunId = searchParams.get('runId');

  const [step, setStep] = useState<number>(STEP.vehicle);
  const [vehicleId, setVehicleId] = useState('');
  const [testType, setTestType] = useState<TestType>('DRIVE');
  const [scenarioId, setScenarioId] = useState('');
  const [routeId, setRouteId] = useState('');
  const [runId, setRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionForm = useForm<RunSessionValues>({ resolver: zodResolver(sessionSchema), defaultValues: { testDate: today() } });
  const conditionsForm = useForm<RunConditionsFormValues>({ resolver: zodResolver(runConditionsFormSchema) });
  const measurementsForm = useForm<RunMeasurementsFormValues>({ resolver: zodResolver(runMeasurementsFormSchema) });

  const vehicles = useQuery(
    orpc.vehicles.list.queryOptions({
      input: { page: 1, pageSize: CATALOGUE_PAGE_SIZE, sortBy: 'code', sortDirection: 'asc', status: 'ACTIVE' },
    }),
  );

  const scenarios = useQuery(
    orpc.scenarios.list.queryOptions({
      input: { page: 1, pageSize: CATALOGUE_PAGE_SIZE, sortBy: 'code', sortDirection: 'asc', status: 'ACTIVE', testType },
    }),
  );

  const selectedVehicle = useMemo(() => (vehicles.data?.items ?? []).find((vehicle) => vehicle.id === vehicleId), [vehicles.data, vehicleId]);
  const selectedScenario = useMemo(() => (scenarios.data?.items ?? []).find((scenario) => scenario.id === scenarioId), [scenarios.data, scenarioId]);

  const isDrive = testType === 'DRIVE';

  const routes = useQuery(
    orpc.routes.list.queryOptions({
      input: {
        page: 1,
        pageSize: CATALOGUE_PAGE_SIZE,
        sortBy: 'code',
        sortDirection: 'asc',
        status: 'ACTIVE',
        routeType: selectedScenario?.routeType ?? undefined,
      },
      // An idle scenario has no route to pick, and a route type is needed to filter meaningfully.
      enabled: isDrive && selectedScenario?.routeType !== undefined && selectedScenario?.routeType !== null,
    }),
  );

  const selectedRoute = useMemo(() => (routes.data?.items ?? []).find((route) => route.id === routeId), [routes.data, routeId]);

  const resumedRun = useQuery(orpc.runs.get.queryOptions({ input: { id: resumeRunId ?? '' }, enabled: resumeRunId !== null }));

  const createDraft = useMutation(orpc.runs.createDraft.mutationOptions());
  const updateConditions = useMutation(orpc.runs.updateConditions.mutationOptions());
  const updateMeasurements = useMutation(orpc.runs.updateMeasurements.mutationOptions());
  const submitRun = useMutation(orpc.runs.submit.mutationOptions());

  const { reset: resetSession } = sessionForm;
  const { reset: resetConditions } = conditionsForm;
  const { reset: resetMeasurements } = measurementsForm;
  const hasResumed = useRef(false);

  // Resuming a draft from the runs list drops the engineer back at the checklist with everything already typed.
  useEffect(() => {
    const run = resumedRun.data;

    if (!run || hasResumed.current) {
      return;
    }

    hasResumed.current = true;
    setRunId(run.id);
    setVehicleId(run.vehicle.id);
    setTestType(run.scenario.testType);
    setScenarioId(run.scenario.id);
    setRouteId(run.route?.id ?? '');
    resetSession(toSessionValues(run));

    if (run.conditions) {
      resetConditions(toConditionsFormValues(run.conditions));
    }

    if (run.measurements) {
      resetMeasurements(toMeasurementsFormValues(run.measurements));
    }

    setStep(STEP.conditions);
  }, [resumedRun.data, resetSession, resetConditions, resetMeasurements]);

  const vehicleOptions: PickerOption[] = (vehicles.data?.items ?? []).map((vehicle) => ({
    id: vehicle.id,
    title: `${vehicle.manufacturer} ${vehicle.model} ${vehicle.modelYear}`,
    subline: vehicle.code,
    detail: `${t(`enums.powertrain.${vehicle.powertrainType}`)} · ${vehicle.segmentName}`,
  }));

  const scenarioOptions: PickerOption[] = (scenarios.data?.items ?? []).map((scenario) => ({
    id: scenario.id,
    title: scenario.code,
    subline: `${scenario.protocolCode} v${scenario.protocolVersion}`,
    detail: [
      t(`enums.season.${scenario.season}`),
      t(`enums.timeBand.${scenario.timeBand}`),
      t(`enums.hvacState.${scenario.hvacState}`),
      scenario.testType === 'IDLE' ? `${scenario.idleDurationMin ?? '?'} min` : t(`enums.routeType.${scenario.routeType ?? 'MIXED'}`),
      // The scenario's expected traffic, so the picker shows which of two otherwise identical scenarios
      // this is — and the checklist below pre-selects it.
      trafficSummary(scenario.trafficLevel, t),
    ]
      .filter((part): part is string => part !== null)
      .join(' · '),
  }));

  const routeOptions: PickerOption[] = (routes.data?.items ?? []).map((route) => ({
    id: route.id,
    title: route.name,
    subline: `${route.code} v${route.version}`,
    // An unsurveyed route is still pickable — it simply has no distance to quote.
    detail: [t(`enums.routeType.${route.routeType}`), route.distanceKm === null ? t('common.notSet') : formatKm(route.distanceKm)].join(' · '),
  }));

  const visibility = {
    isHybrid: selectedVehicle ? isHybridPowertrain(selectedVehicle.powertrainType) : false,
    isDrive,
  };

  const invalidateRunQueries = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.runs.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.runs.get.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.benchmarks.dashboardStats.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.benchmarks.coverageMatrix.key() }),
    ]);
  };

  /** The draft is created once, on the way into the checklist, and patched from then on. */
  const ensureDraft = async (): Promise<string> => {
    if (runId) {
      return runId;
    }

    const draft = await createDraft.mutateAsync({
      vehicleId,
      scenarioId,
      routeId: isDrive && routeId !== '' ? routeId : undefined,
      ...sessionForm.getValues(),
    });

    setRunId(draft.id);

    if (draft.conditions === null && selectedVehicle && selectedScenario) {
      resetConditions(prefillConditions(selectedVehicle, selectedScenario));
    }

    await invalidateRunQueries();

    return draft.id;
  };

  const saveConditions = async (draftId: string): Promise<void> => {
    await updateConditions.mutateAsync({ id: draftId, ...withExplicitNulls(conditionsForm.getValues()) });
  };

  const saveMeasurements = async (draftId: string): Promise<void> => {
    await updateMeasurements.mutateAsync({ id: draftId, ...withExplicitNulls(measurementsForm.getValues()) });
  };

  const onNext = async (): Promise<void> => {
    setError(null);

    try {
      if (step === STEP.vehicle) {
        if (vehicleId === '') {
          setError(t('runs.wizard.errors.vehicle'));

          return;
        }

        setStep(STEP.scenario);

        return;
      }

      if (step === STEP.scenario) {
        if (scenarioId === '') {
          setError(t('runs.wizard.errors.scenario'));

          return;
        }

        setStep(STEP.session);

        return;
      }

      if (step === STEP.session) {
        if (!(await sessionForm.trigger())) {
          return;
        }

        if (isDrive && routeId === '') {
          setError(t('runs.wizard.errors.route'));

          return;
        }

        await ensureDraft();
        setStep(STEP.conditions);

        return;
      }

      if (step === STEP.conditions) {
        if (!(await conditionsForm.trigger())) {
          return;
        }

        await saveConditions(await ensureDraft());
        setStep(STEP.measurements);

        return;
      }

      if (step === STEP.measurements) {
        if (!(await measurementsForm.trigger())) {
          return;
        }

        await saveMeasurements(await ensureDraft());
        setStep(STEP.review);

        return;
      }

      const draftId = await ensureDraft();
      const submitted = await submitRun.mutateAsync({ id: draftId });

      await invalidateRunQueries();
      navigate(`/runs/${submitted.id}`);
    } catch (submitFailure) {
      setError(serverMessage(submitFailure) ?? t(submitErrorKey(submitFailure)));
    }
  };

  /** From the checklist onwards the engineer can stop and come back — the drive happens between the two halves. */
  const onSaveDraft = async (): Promise<void> => {
    setError(null);

    try {
      const draftId = await ensureDraft();

      if (step === STEP.conditions) {
        await saveConditions(draftId);
      } else {
        await saveMeasurements(draftId);
      }

      await invalidateRunQueries();
      navigate('/runs');
    } catch (saveFailure) {
      setError(serverMessage(saveFailure) ?? t(submitErrorKey(saveFailure)));
    }
  };

  const reviewItems: DefinitionItem[] = [
    {
      label: t('runs.wizard.review.vehicle'),
      value: selectedVehicle ? `${selectedVehicle.manufacturer} ${selectedVehicle.model}` : t('common.notSet'),
    },
    { label: t('runs.wizard.review.scenario'), value: selectedScenario ? selectedScenario.code : t('common.notSet') },
    {
      label: t('runs.wizard.review.route'),
      value: selectedRoute ? `${selectedRoute.name} (${selectedRoute.code} v${selectedRoute.version})` : t('common.notSet'),
    },
    { label: t('runs.wizard.review.testDate'), value: formatDay(sessionForm.getValues('testDate')) },
    { label: t('runs.wizard.review.driver'), value: formatText(sessionForm.getValues('driverName')) },
    { label: t('runs.wizard.review.operator'), value: formatText(sessionForm.getValues('operatorName')) },
    { label: t('runs.wizard.review.campaign'), value: formatText(sessionForm.getValues('campaignTag')) },
  ];

  const isBusy = createDraft.isPending || updateConditions.isPending || updateMeasurements.isPending || submitRun.isPending;
  const isLoadingCatalogue = vehicles.isPending || (resumeRunId !== null && resumedRun.isPending);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{runId === null ? t('runs.wizard.title') : t('runs.wizard.resumeTitle')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('runs.wizard.subtitle')}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/runs')}
        >
          <ArrowLeft className="size-3.5" />
          {t('nav.runs')}
        </Button>
      </div>

      <StepIndicator
        labels={STEP_LABEL_KEYS.map((key) => t(key))}
        current={step}
      />

      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {isLoadingCatalogue ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner label={t('app.loading')} />
          {t('app.loading')}
        </div>
      ) : (
        <div className="space-y-4">
          {step === STEP.vehicle ? (
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-foreground">{t('runs.wizard.vehicle.title')}</h2>
              <PickerList
                options={vehicleOptions}
                selectedId={vehicleId}
                onSelect={setVehicleId}
                emptyMessage={t('runs.wizard.vehicle.empty')}
                ariaLabel={t('runs.wizard.vehicle.title')}
              />
            </section>
          ) : null}

          {step === STEP.scenario ? (
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-foreground">{t('runs.wizard.scenario.title')}</h2>

              <div
                role="radiogroup"
                aria-label={t('runs.wizard.scenario.testType')}
                className="flex gap-2"
              >
                {TEST_TYPES.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={testType === value}
                    onClick={() => {
                      setTestType(value);
                      setScenarioId('');
                      setRouteId('');
                    }}
                    className={cn(
                      'h-9 rounded-md border px-4 text-sm font-medium transition-colors',
                      testType === value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {t(`enums.testType.${value}`)}
                  </button>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">{t('runs.wizard.scenario.testTypeHint')}</p>

              <PickerList
                options={scenarioOptions}
                selectedId={scenarioId}
                onSelect={setScenarioId}
                emptyMessage={t('runs.wizard.scenario.empty')}
                ariaLabel={t('runs.wizard.scenario.title')}
              />
            </section>
          ) : null}

          {step === STEP.session ? (
            <div className="space-y-5">
              {isDrive ? (
                <section className="space-y-3">
                  <h2 className="text-sm font-medium text-foreground">{t('runs.wizard.route.title')}</h2>
                  <p className="text-xs text-muted-foreground">
                    {t('runs.wizard.route.filteredBy', { routeType: t(`enums.routeType.${selectedScenario?.routeType ?? 'MIXED'}`) })}
                  </p>
                  <PickerList
                    options={routeOptions}
                    selectedId={routeId}
                    onSelect={setRouteId}
                    emptyMessage={t('runs.wizard.route.empty')}
                    ariaLabel={t('runs.wizard.route.title')}
                  />
                </section>
              ) : (
                <p className="rounded-md border border-border bg-card px-3 py-2 text-sm text-muted-foreground">{t('runs.wizard.route.idle')}</p>
              )}

              <section className="rounded-lg border border-border bg-card p-5">
                <h2 className="text-sm font-medium text-card-foreground">{t('runs.wizard.session.title')}</h2>
                <p className="mt-1 text-xs text-muted-foreground">{t('runs.wizard.session.hint')}</p>

                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <FormField
                    id="testDate"
                    label={t('runs.wizard.session.testDate')}
                    error={sessionForm.formState.errors.testDate?.message}
                  >
                    <Input
                      id="testDate"
                      type="date"
                      hasError={Boolean(sessionForm.formState.errors.testDate)}
                      {...sessionForm.register('testDate')}
                    />
                  </FormField>
                  <FormField
                    id="startTime"
                    label={t('runs.wizard.session.startTime')}
                    error={sessionForm.formState.errors.startTime?.message}
                  >
                    <Input
                      id="startTime"
                      type="time"
                      {...sessionForm.register('startTime', textFieldOptions)}
                    />
                  </FormField>
                  <FormField
                    id="endTime"
                    label={t('runs.wizard.session.endTime')}
                    error={sessionForm.formState.errors.endTime?.message}
                  >
                    <Input
                      id="endTime"
                      type="time"
                      {...sessionForm.register('endTime', textFieldOptions)}
                    />
                  </FormField>
                  <FormField
                    id="driverName"
                    label={t('runs.wizard.session.driver')}
                  >
                    <Input
                      id="driverName"
                      {...sessionForm.register('driverName', textFieldOptions)}
                    />
                  </FormField>
                  <FormField
                    id="operatorName"
                    label={t('runs.wizard.session.operator')}
                  >
                    <Input
                      id="operatorName"
                      {...sessionForm.register('operatorName', textFieldOptions)}
                    />
                  </FormField>
                  <FormField
                    id="campaignTag"
                    label={t('runs.wizard.session.campaign')}
                  >
                    <Input
                      id="campaignTag"
                      className="font-mono"
                      {...sessionForm.register('campaignTag', textFieldOptions)}
                    />
                  </FormField>
                  <FormField
                    id="run-notes"
                    label={t('runs.wizard.session.notes')}
                    className="sm:col-span-2 lg:col-span-3"
                  >
                    <Input
                      id="run-notes"
                      {...sessionForm.register('notes', textFieldOptions)}
                    />
                  </FormField>
                </div>
              </section>
            </div>
          ) : null}

          {step === STEP.conditions ? (
            <form
              id="conditions-form"
              noValidate
              onSubmit={(event) => event.preventDefault()}
              className="space-y-3"
            >
              <p className="text-sm text-muted-foreground">{t('runs.wizard.conditions.hint')}</p>
              <RunFieldGroups
                idPrefix="conditions"
                groups={CONDITION_GROUPS}
                visibility={visibility}
                register={conditionsForm.register}
                errors={conditionsForm.formState.errors}
                hints={{
                  'runs.groups.tires': selectedVehicle?.tireSize
                    ? t('runs.wizard.conditions.tireSizeHint', { tireSize: selectedVehicle.tireSize })
                    : undefined,
                }}
              />
            </form>
          ) : null}

          {step === STEP.measurements ? (
            <form
              id="measurements-form"
              noValidate
              onSubmit={(event) => event.preventDefault()}
              className="space-y-3"
            >
              <p className="text-sm text-muted-foreground">{t('runs.wizard.measurements.hint')}</p>
              <RunFieldGroups
                idPrefix="measurements"
                groups={MEASUREMENT_GROUPS}
                visibility={visibility}
                register={measurementsForm.register}
                errors={measurementsForm.formState.errors}
              />
            </form>
          ) : null}

          {step === STEP.review ? (
            <div className="space-y-4">
              <section className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="text-sm font-medium text-card-foreground">{t('runs.wizard.review.title')}</h2>
                  <StatusText
                    label={t('enums.runStatus.DRAFT')}
                    tone="muted"
                  />
                </div>
                <DefinitionList
                  className="mt-2"
                  items={reviewItems}
                />
              </section>

              <h2 className="text-sm font-medium text-foreground">{t('runs.detail.tabs.conditions')}</h2>
              <RunFieldList
                groups={CONDITION_GROUPS}
                values={conditionsForm.getValues() as Record<string, unknown>}
                visibility={visibility}
                emptyMessage={t('runs.detail.conditionsEmpty')}
              />

              <h2 className="text-sm font-medium text-foreground">{t('runs.detail.tabs.measurements')}</h2>
              <RunFieldList
                groups={MEASUREMENT_GROUPS}
                values={measurementsForm.getValues() as Record<string, unknown>}
                visibility={visibility}
                emptyMessage={t('runs.detail.measurementsEmpty')}
              />

              <p className="text-sm text-muted-foreground">{t('runs.wizard.review.submitHint')}</p>
            </div>
          ) : null}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
        <Button
          variant="outline"
          disabled={step === STEP.vehicle || isBusy}
          onClick={() => setStep(Math.max(STEP.vehicle, step - 1))}
        >
          <ArrowLeft className="size-4" />
          {t('actions.back')}
        </Button>

        <div className="flex flex-wrap gap-2">
          {step >= STEP.conditions && step < STEP.review ? (
            <Button
              variant="outline"
              isLoading={isBusy}
              onClick={() => void onSaveDraft()}
            >
              <Save className="size-4" />
              {t('runs.wizard.saveDraft')}
            </Button>
          ) : null}

          <Button
            isLoading={isBusy}
            onClick={() => void onNext()}
          >
            {step === STEP.review ? (
              <>
                <CheckCircle2 className="size-4" />
                {t('runs.wizard.submit')}
              </>
            ) : (
              <>
                {t('actions.next')}
                <ArrowRight className="size-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
